import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../services/api'
import {
  HiOutlineBookmark,
  HiOutlineTrash,
  HiOutlineShare,
  HiOutlineFire,
  HiOutlineArrowRight,
} from 'react-icons/hi'
import { formatDistanceToNow } from 'date-fns'
import { vi } from 'date-fns/locale'
import toast from 'react-hot-toast'
import clsx from 'clsx'

function SavedNews() {
  const [savedNews, setSavedNews] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadSavedNews()
  }, [])

  const loadSavedNews = async () => {
    try {
      // API endpoint: /api/news/my-saved
      const response = await api.get('/news/my-saved')
      if (response.data.status === 'success') {
        setSavedNews(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to load saved news:', error)
      toast.error('Không thể tải tin đã lưu')
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async (newsId) => {
    try {
      // API endpoint: DELETE /api/news/{newsId}/unsave
      await api.delete(`/news/${newsId}/unsave`)
      setSavedNews(savedNews.filter(item => (item.id === newsId || item.news_id === newsId) ? false : true))
      toast.success('Đã xóa khỏi danh sách lưu')
    } catch (error) {
      console.error('Failed to remove saved news:', error)
      toast.error('Không thể xóa tin')
    }
  }

  const handleShare = async (newsId) => {
    try {
      await api.post(`/news/${newsId}/share`, { customer_type: 'all' })
      toast.success('Đã đánh dấu chia sẻ!')
    } catch (error) {
      console.error('Failed to share news:', error)
      toast.error('Không thể chia sẻ')
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HiOutlineBookmark className="w-7 h-7 mr-3 text-primary-600" />
            Tin đã lưu
          </h1>
          <p className="text-gray-500 mt-1">Các tin tức bạn đã đánh dấu để đọc sau</p>
        </div>

        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="w-24 h-24 bg-gray-200 rounded-lg flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded w-1/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
                  <div className="h-3 bg-gray-200 rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <HiOutlineBookmark className="w-7 h-7 mr-3 text-primary-600" />
          Tin đã lưu
        </h1>
        <p className="text-gray-500 mt-1">
          {savedNews.length > 0
            ? `Bạn đã lưu ${savedNews.length} tin tức`
            : 'Các tin tức bạn đã đánh dấu để đọc sau'}
        </p>
      </div>

      {/* Saved news list */}
      {savedNews.length > 0 ? (
        <div className="space-y-4">
          {savedNews.map((item) => (
            <div
              key={item.id}
              className="card hover:shadow-md transition-all duration-300"
            >
              <div className="flex gap-4 p-4">
                {/* Thumbnail */}
                <Link
                  to={`/news/${item.news_id || item.id}`}
                  className="relative w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden group"
                >
                  {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-lg font-bold">BĐS</span>
                    </div>
                  )}

                  {item.is_hot && (
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-500 text-white">
                        <HiOutlineFire className="w-3 h-3 mr-0.5" />
                        HOT
                      </span>
                    </div>
                  )}
                </Link>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Source & Location */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span className="font-medium text-primary-600">
                      {typeof item.source === 'object' ? item.source?.name : item.source}
                    </span>
                    <span>•</span>
                    <span>
                      {typeof item.location === 'object' ? item.location?.name : item.location}
                    </span>
                  </div>

                  {/* Title */}
                  <Link
                    to={`/news/${item.news_id || item.id}`}
                    className="block"
                  >
                    <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                  </Link>

                  {/* Description */}
                  {item.description && (
                    <p className="text-sm text-gray-500 line-clamp-1 mt-1">
                      {item.description}
                    </p>
                  )}

                  {/* Meta & Actions */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>
                        Đã lưu{' '}
                        {item.saved_at && formatDistanceToNow(new Date(item.saved_at), {
                          addSuffix: true,
                          locale: vi,
                        })}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleShare(item.news_id || item.id)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Chia sẻ"
                      >
                        <HiOutlineShare className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleRemove(item.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Xóa"
                      >
                        <HiOutlineTrash className="w-5 h-5" />
                      </button>
                      <Link
                        to={`/news/${item.news_id || item.id}`}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Xem chi tiết"
                      >
                        <HiOutlineArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <HiOutlineBookmark className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Chưa có tin đã lưu
          </h3>
          <p className="text-gray-500 mb-6">
            Lưu các tin tức quan trọng để đọc sau và chia sẻ với khách hàng
          </p>
          <Link
            to="/news"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Khám phá tin tức
            <HiOutlineArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      )}
    </div>
  )
}

export default SavedNews
